import type { IPixellotPlayerSource, IPixellotPlayerSourceType } from "~~/modules/pixellot-sdk";

import type { IResponseClip, IResponseHighlight, IResponsePlayerHighlight } from "./event-clips";
import type { IResponseEvent } from "./events";
import type { IResponsePlaylist } from "./playlists";

export type TVideoType = "event" | "mbe-event" | "clip" | "highlight" | "autohighlight" | "player-highlight";

export const VIDEO_SOURCE_TYPE = {
  CONTINUOUS: "continuous",
  CLIPPED: "clipped",
} as const;
export type TVideoSourceType = (typeof VIDEO_SOURCE_TYPE)[keyof typeof VIDEO_SOURCE_TYPE];

export type TVideoAngleSource = IPixellotPlayerSource & {
  id?: string;
  mp4_url?: string;
  duration?: number;
  index?: number;
  videoType?: TVideoSourceType;
  primary?: boolean;
};

export type TContinuousVideoAngle = TVideoAngleSource;

export type TClippedVideoAngle = Partial<TVideoAngleSource> & {
  videos: TVideoAngleSource[];
};

export type TBasicVideoAngle = TContinuousVideoAngle | TClippedVideoAngle;
export type TBasicVideoAngles = Array<TBasicVideoAngle | TClippedVideoAngle>;
export type TContinuousAnglesMap = Map<IPixellotPlayerSourceType, TContinuousVideoAngle>;
export type TClippedAnglesMap = Map<IPixellotPlayerSourceType, TClippedVideoAngle>;

export interface IVideoAngles {
  continuous?: TContinuousAnglesMap;
  clipped?: TClippedAnglesMap;
}

export interface IBasicVideo {
  id: string;
  eventId: string; // Every video should have associated eventId.
  eventAbeId?: string;
  favoriteId?: string;
  type: TVideoType;
  source_type: "video";
  title: string;
  created_at: string | Date;
  views_count: number;
  video_duration?: number;
  poster?: string;
  angles: IVideoAngles;
  upload_source_type?: TVideoSourceType;
  is_favorite: boolean;
  permissions: {
    canAddToFavorites: boolean;
    canDownloadVideoWithoutLogin: boolean;
  };
}

export interface IResponseFavoriteVideos {
  data: IResponseFavoriteVideo[];
  meta: {
    total: number;
  };
}

export interface IResponseFavoriteVideo {
  id: string;
  type: TVideoType | "playlist";
  attributes:
    | IResponseEvent["attributes"]
    | IResponseClip["attributes"]
    | IResponseHighlight["attributes"]
    | IResponsePlayerHighlight["attributes"]
    | IResponsePlaylist["attributes"];
  relationships?: {
    favorite?: IResponseFavoriteVideo;
  };
  meta: {
    createdAt: Date | string;
    updatedAt: Date | string;
  };
}
